import { useEffect, useRef } from 'react'
import { useAlerts } from '../lib/api'
import { useDesktopNotif } from '../lib/useDesktopNotif'

const SEVERITY_LABEL: Record<string, string> = {
  critical: '🔴 Critique',
  high:     '🟠 Haute',
  medium:   '🟡 Moyenne',
  low:      '🔵 Faible',
}

/**
 * Composant invisible qui surveille les alertes critiques/haute et envoie
 * une notification desktop si une nouvelle alerte apparaît.
 * Déduplique par ID d'alerte (Set en mémoire — réinitialisé à chaque montage).
 */
export default function NotificationManager() {
  const { data } = useAlerts()
  const { send, permission, enabled } = useDesktopNotif()
  const notifiedIds = useRef(new Set<string>())
  const firstRun = useRef(true)

  useEffect(() => {
    if (!data?.alerts || permission !== 'granted' || !enabled) return

    const critiques = data.alerts.filter(
      (a) => (a.severity === 'critical' || a.severity === 'high') && !a.ackedAt,
    )

    if (firstRun.current) {
      // Au premier chargement : on marque les alertes existantes comme déjà vues
      // pour ne pas spammer l'utilisateur au démarrage
      critiques.forEach((a) => notifiedIds.current.add(a.id))
      firstRun.current = false
      return
    }

    for (const alert of critiques) {
      if (notifiedIds.current.has(alert.id)) continue
      notifiedIds.current.add(alert.id)

      send(`${SEVERITY_LABEL[alert.severity] ?? alert.severity} — ${alert.projectId}`, {
        body: alert.message,
        tag:  alert.id,        // évite les doublons au niveau OS
        requireInteraction: alert.severity === 'critical',
      })
    }
  }, [data, permission, enabled, send])

  // Ce composant ne rend rien — il est uniquement logique
  return null
}
